import { APP_CONFIG } from '../shared/config/app.config';
import { AuthService } from '../auth/services/authService';
import type { SemanaDeEntrenos, WorkoutSession } from './types';
import type { RoutineDifficulty } from '../routines/types/routine';

const BASE = APP_CONFIG.API.BASE_URL;

function authHeaders(): HeadersInit {
  const token = AuthService.getToken();
  return token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
}

async function unwrap<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as {
      message?: string;
    } | null;
    throw new Error(body?.message ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export const WorkoutsService = {
  /**
   * Abre una sesion sobre una rutina.
   *
   * `difficulty` es el nivel que se estaba viendo. Hace falta para las siete
   * rutinas de la semana: de esas solo se guarda la variante intermedia, y el
   * backend congela dentro de la sesion la que de verdad se va a entrenar. Sin
   * mandarlo, un principiante quedaria registrado con las series del
   * intermedio.
   */
  start(
    routineId: string,
    difficulty?: RoutineDifficulty,
  ): Promise<WorkoutSession> {
    return fetch(`${BASE}/workouts/start/${routineId}`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(difficulty ? { difficulty } : {}),
    }).then((r) => unwrap<WorkoutSession>(r));
  },

  /**
   * Mi semana: lo entrenado de este lunes a este domingo.
   *
   * La ventana la calcula el backend en la hora del gimnasio y se corre sola,
   * asi que la semana empieza vacia sin que nadie tenga que reiniciar nada.
   */
  miSemana(): Promise<SemanaDeEntrenos> {
    return fetch(`${BASE}/workouts/mine/week`, { headers: authHeaders() }).then(
      (r) => unwrap<SemanaDeEntrenos>(r),
    );
  },

  listMine(): Promise<WorkoutSession[]> {
    return fetch(`${BASE}/workouts/mine`, { headers: authHeaders() }).then((r) =>
      unwrap<WorkoutSession[]>(r),
    );
  },

  getOne(id: string): Promise<WorkoutSession> {
    return fetch(`${BASE}/workouts/${id}`, { headers: authHeaders() }).then((r) =>
      unwrap<WorkoutSession>(r),
    );
  },

  logSet(
    id: string,
    payload: {
      routineExerciseId: string;
      setNumber: number;
      weight?: number;
      reps?: number;
      notes?: string;
    },
  ): Promise<WorkoutSession> {
    return fetch(`${BASE}/workouts/${id}/sets`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    }).then((r) => unwrap<WorkoutSession>(r));
  },

  complete(id: string, notes?: string): Promise<WorkoutSession> {
    return fetch(`${BASE}/workouts/${id}/complete`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify({ notes }),
    }).then((r) => unwrap<WorkoutSession>(r));
  },

  abandon(id: string): Promise<void> {
    return fetch(`${BASE}/workouts/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    }).then((r) => {
      if (!r.ok && r.status !== 204) throw new Error(`HTTP ${r.status}`);
    });
  },
};
